import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  orderBy, 
  limit, 
  runTransaction, 
  serverTimestamp 
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { getISTDate } from "../utils/dateUtils";

function DistributorInventoryReport() {
  const [userProfile, setUserProfile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skuMaster, setSkuMaster] = useState({});
  const [date, setDate] = useState(getISTDate());
  const navigate = useNavigate();

  const [showExportModal, setShowExportModal] = useState(false);
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/");
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        if (profile.role !== "distributor" && profile.role !== "superadmin") {
          navigate("/");
          return;
        }
        setUserProfile(profile);
        loadInventory(profile, date);
      }
    });
    return () => unsub();
  }, [navigate, date]);

  const handleExportCsv = async () => {
    if (!fromDate || !toDate) { alert("Please select both dates"); return; }
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
    
    if (diffDays > 60) {
      alert("Maximum 60 days range allowed for export.");
      return;
    }

    setIsExporting(true);
    try {
      // Use a simple query to avoid composite index requirement
      const q = query(
        collection(db, "distributor_stock_daily"),
        where("distributorId", "==", userProfile.partyId)
      );
      const snap = await getDocs(q);
      
      const csvRows = [["Date", "SKU Name", "Opening", "Receipt", "Sales", "Closing"]];
      
      const sortedDocs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(d => d.date >= fromDate && d.date <= toDate)
        .sort((a, b) => a.date.localeCompare(b.date));

      sortedDocs.forEach(d => {
        const dateStr = d.date;
        Object.entries(d.inventory || {}).forEach(([sku, info]) => {
          const name = skuMaster[sku]?.Name || sku;
          const open = (info.opening || 0).toFixed(2);
          const recv = (info.received || 0).toFixed(2);
          const sold = (info.sold || 0).toFixed(2);
          const close = ((info.opening || 0) + (info.received || 0) - (info.sold || 0)).toFixed(2);
          csvRows.push([dateStr, name, open, recv, sold, close]);
        });
      });

      const csvString = csvRows.map(row => row.join(",")).join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `StockLedger_${userProfile.partyId}_${fromDate}_to_${toDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setShowExportModal(false);
    } catch (err) {
      console.error(err);
      alert("Export failed: " + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  const getOrInitDistributorStock = async (distId, targetDate, clientId) => {
    const docId = `${distId}_${targetDate}`;
    const stockRef = doc(db, "distributor_stock_daily", docId);
    
    return await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(stockRef);
      const existingData = snap.exists() ? snap.data() : null;

      if (existingData && existingData.carryForwardDone) return existingData;

      const q = query(
        collection(db, "distributor_stock_daily"),
        where("distributorId", "==", distId),
        where("date", "<", targetDate),
        orderBy("date", "desc"),
        limit(1)
      );
      const lastSnap = await getDocs(q);
      
      const allFields = snap.exists() ? snap.data() : {};
      const baseInventory = allFields.inventory || {};
      
      const normalizedExisting = {};
      Object.entries(allFields).forEach(([key, val]) => {
          if (key.startsWith("inventory.") && key.includes(".", 10)) {
              const match = key.match(/^inventory\.(.*)\.(received|sold|opening|billed)$/);
              if (match) {
                  const sku = match[1].trim().toUpperCase();
                  const field = (match[2] === "billed" || match[2] === "sold") ? "sold" : match[2];
                  if (!normalizedExisting[sku]) normalizedExisting[sku] = { opening: 0, received: 0, sold: 0 };
                  const numVal = (typeof val === 'object' && val?.iv) ? (val.iv || 0) : Number(val || 0);
                  normalizedExisting[sku][field] += numVal;
              }
          }
      });
      // Merge structured inventory
      Object.entries(baseInventory).forEach(([sku, info]) => {
          const skuUp = sku.trim().toUpperCase();
          if (!normalizedExisting[skuUp]) normalizedExisting[skuUp] = { opening: 0, received: 0, sold: 0 };
          if (typeof info === 'object') {
              if (info.opening) normalizedExisting[skuUp].opening += Number(info.opening || 0);
              if (info.received) normalizedExisting[skuUp].received += Number(info.received || 0);
              if (info.sold || info.billed) normalizedExisting[skuUp].sold += Number(info.sold || info.billed || 0);
          }
      });

      let newInventory = {};
      if (!lastSnap.empty) {
          const lastData = lastSnap.docs[0].data();
          Object.entries(lastData.inventory || {}).forEach(([sku, info]) => {
              const closing = (info.opening || 0) + (info.received || 0) - (info.sold || 0);
              if (closing !== 0) {
                  newInventory[sku] = { opening: closing, received: 0, sold: 0 };
              }
          });
      }

      // Final Merge
      Object.entries(normalizedExisting).forEach(([sku, info]) => {
          if (!newInventory[sku]) newInventory[sku] = { opening: 0, received: 0, sold: 0 };
          newInventory[sku].received += info.received;
          newInventory[sku].sold += info.sold;
      });

      const payload = {
          distributorId: distId,
          date: targetDate,
          inventory: newInventory,
          clientId: clientId,
          carryForwardDone: true,
          initialized: true,
          updatedAt: serverTimestamp()
      };

      transaction.set(stockRef, payload, { merge: true });
      return payload;
    });
  };

  const loadInventory = async (profile, targetDate) => {
    setLoading(true);
    try {
      const skuSnap = await getDocs(collection(db, "skuMaster"));
      const smap = {};
      skuSnap.forEach(d => {
        const data = d.data();
        smap[data.sku] = data;
      });
      setSkuMaster(smap);

      const distId = profile.partyId;
      if (!distId) {
        setLoading(false);
        return;
      }

      const stockData = await getOrInitDistributorStock(distId, targetDate, profile.clientId);
      const data = stockData.inventory || {};

      // Build a merged, case-insensitive lookup map to handle legacy fragmented records
      const normalizedData = {};
      Object.entries(data).forEach(([key, info]) => {
          let sku = key;
          let field = null;

          // Legacy Reconciliation: Handle literal dot-notation keys if present
          if (key.startsWith("inventory.")) {
              const match = key.match(/^inventory\.(.*)\.(received|sold|opening|billed)$/);
              if (match) {
                  sku = match[1];
                  field = match[2];
              }
          }

          const skuUp = sku.trim().toUpperCase();
          if (!normalizedData[skuUp]) {
              normalizedData[skuUp] = { opening: 0, received: 0, sold: 0 };
          }

          if (field) {
              // Map 'billed' (DS source) to 'sold' for distributor perspective
              const targetField = (field === "billed" || field === "sold") ? "sold" : field;
              const val = (typeof info === 'object' && info?.iv) ? (info.iv || 0) : Number(info || 0); // Handle increment objects if they exist in state
              normalizedData[skuUp][targetField] += val;
          } else {
              if (typeof info === 'object') {
                  if (info.opening) normalizedData[skuUp].opening += Number(info.opening || 0);
                  if (info.received) normalizedData[skuUp].received += Number(info.received || 0);
                  if (info.sold || info.billed) normalizedData[skuUp].sold += Number(info.sold || info.billed || 0);
              }
          }
      });

      const finalRows = Object.keys(smap).map(sku => {
          const skuKey = sku.trim().toUpperCase();
          const info = normalizedData[skuKey] || { opening: 0, received: 0, sold: 0 };
          const closing = (info.opening || 0) + (info.received || 0) - (info.sold || 0);
          return {
              sku,
              name: smap[sku]?.Name || sku,
              opening: info.opening || 0,
              received: info.received || 0,
              sold: info.sold || 0,
              closing: closing
          };
      });
      setRows(finalRows);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Layout><div style={{ padding: 100, textAlign: 'center' }}>Synchronizing Stock Ledger...</div></Layout>;

  return (
    <Layout>
      <div className="layout-content">
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Stock Ledger</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Inventory movement tracking for <strong>{userProfile?.name || userProfile?.partyId}</strong></p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button 
                  onClick={() => setShowExportModal(true)}
                  className="btn-secondary"
                  style={{ padding: "10px 20px", borderRadius: 12, fontWeight: 800, fontSize: 13 }}
                >
                  📥 EXCEL EXPORT
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: 16, background: "var(--surface)", padding: "8px 16px", borderRadius: 12, border: "1px solid var(--border)" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>AUDIT DATE:</span>
                    <input 
                        type="date" 
                        value={date} 
                        onChange={e => setDate(e.target.value)}
                        style={{ border: "none", background: "transparent", fontSize: 16, fontWeight: 800, color: "var(--primary)", outline: "none", cursor: "pointer" }}
                    />
                </div>
            </div>
        </div>

        {showExportModal && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div className="premium-card" style={{ width: 400, padding: 32 }}>
              <h3 style={{ margin: "0 0 24px 0", fontSize: 20, fontWeight: 900 }}>Export Stock Ledger</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8 }}>FROM DATE</label>
                  <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="form-input" style={{ width: "100%" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8 }}>TO DATE</label>
                  <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="form-input" style={{ width: "100%" }} />
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", background: "var(--surface-hover)", padding: 12, borderRadius: 8 }}>
                  Note: Maximum 60 days can be exported at once to maintain system speed.
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                  <button onClick={() => setShowExportModal(false)} className="btn-secondary" style={{ flex: 1 }}>CANCEL</button>
                  <button onClick={handleExportCsv} disabled={isExporting} className="btn-primary" style={{ flex: 1 }}>
                    {isExporting ? "EXPORTING..." : "DOWNLOAD CSV"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="enterprise-table">
                <thead>
                    <tr>
                        <th style={{ paddingLeft: 24 }}>PRODUCT DESCRIPTION</th>
                        <th style={{ textAlign: "right" }}>OPENING</th>
                        <th style={{ textAlign: "right" }}>RECEIPT</th>
                        <th style={{ textAlign: "right" }}>SALES</th>
                        <th style={{ textAlign: "right", paddingRight: 24 }}>CLOSING</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan="5" style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>No stock activity recorded globally for this period.</td>
                        </tr>
                    ) : (
                        rows.map(r => (
                            <tr key={r.sku}>
                                <td style={{ paddingLeft: 24 }}>
                                    <div style={{ fontWeight: 800, color: "var(--text-main)", fontSize: 15 }}>{r.name}</div>
                                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.sku}</div>
                                </td>
                                <td style={{ textAlign: "right", fontWeight: 700 }}>{r.opening.toFixed(2)}</td>
                                <td style={{ textAlign: "right", color: "var(--success)", fontWeight: 800 }}>{r.received.toFixed(2)}</td>
                                <td style={{ textAlign: "right", color: "var(--error)", fontWeight: 800 }}>{r.sold.toFixed(2)}</td>
                                <td style={{ textAlign: "right", paddingRight: 24, fontWeight: 900 }}>{r.closing.toFixed(2)}</td>
                            </tr>
                        )))}
                </tbody>
            </table>
        </div>

        <div style={{ marginTop: 24, padding: 16, background: "var(--surface-hover)", borderRadius: 12, border: "1px dashed var(--border)" }}>
            <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
                * All quantities are reflected in Packets. One Bag/Carton contains multiple packets based on SKU configuration. Receipt values sync from C&F Invoices; Sales sync from verified DS billings.
            </p>
        </div>

        {/* Audit Diagnostics */}
        <div style={{ marginTop: 40, borderTop: "1px solid var(--border)", paddingTop: 16, opacity: 0.4 }}>
            <details>
                <summary style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", cursor: "pointer" }}>AUDIT DIAGNOSTICS</summary>
                <div style={{ fontSize: 10, marginTop: 8, fontFamily: "monospace" }}>
                    Distributor ID: {userProfile?.partyId}<br/>
                    Target Date: {date}<br/>
                    Document Path: distributor_stock_daily/{userProfile?.partyId}_{date}
                </div>
            </details>
        </div>
      </div>
    </Layout>
  );
}

export default DistributorInventoryReport;
